import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import {
  Box, Alert, Button, Collapse, useMediaQuery, useTheme,
} from '@mui/material';
import DispatcherBookDrawer, { BookMode } from '../../components/dispatcher/DispatcherBookDrawer';
import DispatchTopBar, { type DispatchBookMode } from '../../components/dispatcher/DispatchTopBar';
import { selectIsSuperAdmin } from '../../store/slices/authSlice';
import { selectEnabledModules } from '../../store/slices/tenantSlice';
import DispatchQueuePanel from '../../components/dispatcher/DispatchQueuePanel';
import DispatchDriverPanel from '../../components/dispatcher/DispatchDriverPanel';
import DispatchDashboardWidgets from '../../components/dispatcher/DispatchDashboardWidgets';
import DispatchLiveMap from '../../components/dispatcher/DispatchLiveMap';
import DispatchMobileNav, { DispatchMobileView } from '../../components/dispatcher/DispatchMobileNav';
import { buildDispatchRows } from '../../components/dispatcher/dispatchData';
import { DispatchRow, ServiceTab, QueueStatusTab, statusToTab, countRowsByQueueTab, resolveDispatchBookMode, dispatchBookCopy } from '../../components/dispatcher/dispatchTypes';
import { DISPATCH, panelShell, dispatchPageBleed, dispatchPageHeight } from '../../components/dispatcher/dispatchTheme';
import api from '../../services/api';
import { getDispatchDashboard, getDispatchFleet, getDispatchHeatMap, DispatchHeatMapRange, DispatchHeatMapService } from '../../services/dispatcherService';
import { useDispatchSocket } from '../../hooks/useDispatchSocket';
import { useBackendHealth } from '../../hooks/useBackendHealth';

const POLL_MS = 10000;

const DISPATCH_TABS: QueueStatusTab[] = ['action', 'active', 'done', 'cancelled'];

function parseDispatchTab(raw: string | null): QueueStatusTab {
  return DISPATCH_TABS.includes(raw as QueueStatusTab) ? (raw as QueueStatusTab) : 'action';
}

const BOOK_MODULE_MAP: { mode: DispatchBookMode; module: string }[] = [
  { mode: 'ride', module: 'transportation' },
  { mode: 'porter', module: 'porter' },
  { mode: 'on_demand', module: 'on_demand' },
];

export default function DispatcherPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isThreeCol = useMediaQuery(theme.breakpoints.up('lg'));
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const enabledModules = useSelector(selectEnabledModules);
  const enabledBookModes = useMemo(
    () => BOOK_MODULE_MAP.filter((m) => isSuperAdmin || enabledModules.includes(m.module)).map((m) => m.mode),
    [isSuperAdmin, enabledModules],
  );

  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isBackendUp } = useBackendHealth();
  const [bookMode, setBookMode] = useState<BookMode>(null);
  const [selected, setSelected] = useState<DispatchRow | null>(null);
  const [serviceFilter, setServiceFilter] = useState<ServiceTab>('all');
  const activeBookMode = useMemo(
    () => resolveDispatchBookMode(serviceFilter, enabledBookModes),
    [serviceFilter, enabledBookModes],
  );
  const [statusTab, setStatusTab] = useState<QueueStatusTab>(() => parseDispatchTab(searchParams.get('tab')));
  const [search, setSearch] = useState('');
  const [mobileView, setMobileView] = useState<DispatchMobileView>('queue');
  const [statsExpanded, setStatsExpanded] = useState(false);
  const [heatMapRange, setHeatMapRange] = useState<DispatchHeatMapRange>('live');
  const [showHeatMap, setShowHeatMap] = useState(true);

  useEffect(() => {
    setStatusTab(parseDispatchTab(searchParams.get('tab')));
  }, [searchParams]);

  const handleStatusTabChange = (tab: QueueStatusTab) => {
    setStatusTab(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'action') next.delete('tab');
      else next.set('tab', tab);
      return next;
    }, { replace: true });
  };

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['dispatch-dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['dispatch-heatmap'] });
    queryClient.invalidateQueries({ queryKey: ['dispatch-fleet'] });
    queryClient.invalidateQueries({ queryKey: ['dispatcher-rides'] });
    queryClient.invalidateQueries({ queryKey: ['dispatcher-porter'] });
    queryClient.invalidateQueries({ queryKey: ['dispatcher-on-demand'] });
    queryClient.invalidateQueries({ queryKey: ['dispatcher-delivery-hub'] });
    queryClient.invalidateQueries({ queryKey: ['dispatch-roster'] });
    queryClient.invalidateQueries({ queryKey: ['available-drivers'] });
  };

  useDispatchSocket(() => invalidateAll());

  const pollEnabled = isBackendUp;
  const pollInterval = pollEnabled ? POLL_MS : false;

  const { data: dashboardStats, isLoading: dashboardLoading } = useQuery({
    queryKey: ['dispatch-dashboard'],
    queryFn: getDispatchDashboard,
    refetchInterval: pollInterval,
    enabled: pollEnabled,
  });

  const { data: fleet } = useQuery({
    queryKey: ['dispatch-fleet'],
    queryFn: getDispatchFleet,
    refetchInterval: pollInterval,
    enabled: pollEnabled,
  });

  const heatMapService: DispatchHeatMapService =
    serviceFilter === 'transportation' ? serviceFilter : 'all';

  const { data: heatMapData, isLoading: heatMapLoading } = useQuery({
    queryKey: ['dispatch-heatmap', heatMapRange, heatMapService],
    queryFn: () => getDispatchHeatMap({ range: heatMapRange, service: heatMapService }),
    refetchInterval: heatMapRange === 'live' ? pollInterval : false,
    enabled: pollEnabled,
  });

  const { data: rides, isLoading: ridesLoading, isError: ridesError } = useQuery({
    queryKey: ['dispatcher-rides'],
    queryFn: async () => (await api.get('/rides', { params: { limit: 200 } })).data.data,
    refetchInterval: pollInterval,
    enabled: pollEnabled,
  });

  const { data: porterOrdersRaw, isLoading: porterLoading, isError: porterError } = useQuery({
    queryKey: ['dispatcher-porter'],
    queryFn: async () => (await api.get('/porter/orders', { params: { limit: 200 } })).data,
    refetchInterval: pollInterval,
    enabled: pollEnabled,
  });

  const { data: onDemandBookings, isLoading: onDemandLoading, isError: onDemandError } = useQuery({
    queryKey: ['dispatcher-on-demand'],
    queryFn: async () => (await api.get('/on-demand/bookings', { params: { limit: 200 } })).data.data,
    refetchInterval: pollInterval,
    enabled: pollEnabled,
  });

  const porterOrders = useMemo(() => {
    const payload = porterOrdersRaw?.data;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.items)) return payload.items;
    return [];
  }, [porterOrdersRaw]);

  const allRows = useMemo(
    () => buildDispatchRows(rides, porterOrders, onDemandBookings),
    [rides, porterOrders, onDemandBookings],
  );

  const rows = useMemo(() => {
    if (serviceFilter === 'all') return allRows;
    return allRows.filter((r) => r.service === serviceFilter);
  }, [allRows, serviceFilter]);

  const isLoading = ridesLoading || porterLoading || onDemandLoading;
  const hasLoadError = ridesError || porterError || onDemandError;
  const allTabCounts = useMemo(() => countRowsByQueueTab(allRows), [allRows]);

  const activeCount = isLoading ? (dashboardStats?.activeJobs ?? 0) : allTabCounts.active;
  const pendingCount = isLoading ? (dashboardStats?.pendingAssignments ?? 0) : allTabCounts.action;

  const selectedKey = selected ? `${selected.service}-${selected._id}` : null;

  useEffect(() => {
    if (!selected) return;
    const exists = rows.some((r) => r._id === selected._id && r.service === selected.service);
    if (!exists) setSelected(null);
  }, [rows, selected]);

  useEffect(() => {
    setSelected((prev) => {
      if (!prev) return prev;
      return allRows.find((r) => `${r.service}-${r._id}` === `${prev.service}-${prev._id}`) || prev;
    });
  }, [allRows]);

  useEffect(() => {
    if (!selected) return;
    setStatusTab(statusToTab(selected.status));
  }, [selected?._id, selected?.service, selected?.status]);

  const handleSelect = (row: DispatchRow) => {
    setSelected(row);
    setStatusTab(statusToTab(row.status));
    if (isMobile) setMobileView('detail');
  };

  const panelVisible = (panel: DispatchMobileView) => !isMobile || mobileView === panel;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...dispatchPageBleed,
        ...dispatchPageHeight,
        background: DISPATCH.bgSoft,
        overflow: 'hidden',
      }}
    >
      <DispatchTopBar
        serviceFilter={serviceFilter}
        onServiceFilterChange={setServiceFilter}
        activeCount={activeCount}
        pendingCount={pendingCount}
        onRefresh={invalidateAll}
        enabledBookModes={enabledBookModes}
        onBook={(mode) => setBookMode(mode)}
        statsExpanded={statsExpanded}
        onToggleStats={() => setStatsExpanded((v) => !v)}
      />

      <Collapse in={statsExpanded}>
        <Box sx={{ ...panelShell(), mx: { xs: 1, md: 1.5 }, mt: { xs: 0.75, md: 1 } }}>
          <DispatchDashboardWidgets stats={dashboardStats} loading={dashboardLoading} compact={isMobile} />
        </Box>
      </Collapse>

      {hasLoadError && (
        <Alert
          severity="warning"
          sx={{ mx: { xs: 1, md: 1.5 }, mt: 0.75, borderRadius: `${DISPATCH.radiusSm}px` }}
          action={<Button color="inherit" size="small" onClick={invalidateAll}>Retry</Button>}
        >
          {!isBackendUp
            ? (import.meta.env.DEV
              ? 'API server is offline. Start backend (npm run dev) — feeds resume automatically when it is back.'
              : 'Unable to load live data. Retry in a moment.')
            : 'Some feeds failed to load. Data may be incomplete.'}
        </Alert>
      )}

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          minHeight: 0,
          minWidth: 0,
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          gap: { xs: 0, lg: 1 },
          px: { xs: 0, lg: 1 },
          py: { xs: 0, lg: 0.75 },
        }}
      >
        {/* Queue */}
        <Box
          sx={{
            width: { xs: '100%', lg: DISPATCH.queueWidth.lg },
            maxWidth: { lg: DISPATCH.queueWidth.lg },
            flexShrink: 0,
            flex: { xs: 1, lg: '0 0 auto' },
            minWidth: 0,
            minHeight: 0,
            display: panelVisible('queue') ? 'flex' : 'none',
            flexDirection: 'column',
            overflow: 'hidden',
            ...panelShell({ height: '100%', minHeight: 0 }),
          }}
        >
          <DispatchQueuePanel
            rows={rows}
            loading={isLoading}
            selectedId={selectedKey}
            statusTab={statusTab}
            onStatusTabChange={handleStatusTabChange}
            search={search}
            onSearchChange={setSearch}
            onSelect={handleSelect}
          />
        </Box>

        {/* Map — center column gets maximum space */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: { xs: 360, lg: 0 },
            order: { xs: 2, lg: 2 },
            display: panelVisible('map') ? 'flex' : 'none',
            flexDirection: 'column',
            ...panelShell({ minHeight: { xs: 360, lg: 280 }, height: { xs: '100%', lg: '100%' }, borderRadius: { xs: 0, lg: `${DISPATCH.radius}px` } }),
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
            <DispatchLiveMap
              selected={selected}
              fleet={fleet}
              heatMapPoints={heatMapData?.points}
              heatMapRange={heatMapRange}
              onHeatMapRangeChange={setHeatMapRange}
              showHeatMap={showHeatMap}
              onToggleHeatMap={() => setShowHeatMap((v) => !v)}
              totalDemand={heatMapData?.totalDemand}
              heatMapLoading={heatMapLoading}
              height="100%"
            />
          </Box>
        </Box>

        {/* Detail */}
        <Box
          sx={{
            width: { xs: '100%', lg: DISPATCH.detailWidth.lg },
            maxWidth: { lg: DISPATCH.detailWidth.lg },
            flexShrink: 0,
            flex: { xs: 1, lg: '0 0 auto' },
            minWidth: 0,
            minHeight: 0,
            overflow: 'hidden',
            display: (isThreeCol && !isMobile) || panelVisible('detail') ? 'flex' : 'none',
            flexDirection: 'column',
            ...panelShell({ height: '100%' }),
          }}
        >
          <DispatchDriverPanel
            selected={selected}
            onAssigned={invalidateAll}
            onBook={() => setBookMode(activeBookMode)}
            bookCtaLabel={dispatchBookCopy(activeBookMode).createNew}
            onClear={() => { setSelected(null); if (isMobile) setMobileView('queue'); }}
          />
        </Box>
      </Box>

      <DispatchMobileNav
        value={mobileView}
        onChange={setMobileView}
        detailDisabled={!selected}
      />

      <DispatcherBookDrawer
        mode={bookMode}
        onClose={() => setBookMode(null)}
        onSuccess={() => { invalidateAll(); setBookMode(null); }}
      />
    </Box>
  );
}
